import { useTransition } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { ActivationAlertsContractAlertsDataType } from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { useToasts } from "app/common/hooks";
import ExcelClientDownload from "app/components/React/ExcelButton/ExcelClientDownload";

import { useCommonAlertsQueryParams } from "../useCommonAlertsQueryParams";

export const useDownloadAlertsExcel = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
    const { translate } = useTranslate();
    const { showSuccessToast, showErrorToast } = useToasts();
    const { activationId, competitors, ...commonParams } = useCommonAlertsQueryParams();
    const [isPending, startTransition] = useTransition();

    const download = (dataTypes: ActivationAlertsContractAlertsDataType[]) => {
        startTransition(async () => {
            try {
                const urlParams = new URLSearchParams(commonParams);
                dataTypes.forEach((dataType) => {
                    urlParams.append("dataTypes", dataType);
                });
                competitors.forEach((competitor) => {
                    urlParams.append("competitors", competitor);
                });

                const { success, error } = await ExcelClientDownload(
                    `/api/activation/${activationId}/alerts/excel?${urlParams}`,
                );

                if (success) {
                    showSuccessToast(translate("paid.excel.dialog.download.success"));
                    onSuccess?.();
                } else {
                    throw error ?? new Error("Excel download failed");
                }
            } catch (error) {
                Sentry.errorTracker({
                    message: "Error downloading alerts Excel",
                    e: error,
                    level: "error",
                    params: {
                        extra: {
                            ...commonParams,
                            dataTypes,
                        },
                    },
                });

                showErrorToast(translate("global.download.excel.error"));
            }
        });
    };

    return { download, isPending };
};
